import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconSortAscending from '@apollo/icons/default/IconSortAscending.svg';
import IconSortDescending from '@apollo/icons/default/IconSortDescending.svg';
import {
  Button,
  ButtonProps,
  Heading,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Radio,
} from '@apollo/orbit';
import React, { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

function getSortColumnTitle(sortColumn: GraphQLTypes.PersistedQuerySortColumn) {
  switch (sortColumn) {
    case GraphQLTypes.PersistedQuerySortColumn.FIRST_PUBLISHED_AT:
      return 'First published';
    case GraphQLTypes.PersistedQuerySortColumn.LAST_PUBLISHED_AT:
      return 'Last published';
    case GraphQLTypes.PersistedQuerySortColumn.NAME:
      return 'Name';
    default:
      return assertUnreachableOrReturnDefault(sortColumn, 'Unknown');
  }
}

export const PersistedQuerySortingControls = ({
  className,
  sortColumn,
  sortDirection,
  size,
  variant,
}: {
  className?: string;
  sortColumn: GraphQLTypes.PersistedQuerySortColumn;
  sortDirection: GraphQLTypes.Ordering;
} & Pick<ButtonProps, 'size' | 'variant'>) => {
  const { currentPersistedQueriesListRoute } = useInternalGraphLinking();
  const history = useHistory();
  const location = useLocation();

  const setUrlSortParams = useCallback(
    ({
      sortColumn: newSortColumn,
      sortDirection: newSortDirection,
    }: {
      sortColumn?: GraphQLTypes.PersistedQuerySortColumn;
      sortDirection?: GraphQLTypes.Ordering;
    }) => {
      history.push(
        currentPersistedQueriesListRoute.locationFrom({
          location,
          patch: {
            sortColumn: newSortColumn,
            sortDirection: newSortDirection,
          },
        }),
      );
    },
    [history, currentPersistedQueriesListRoute, location],
  );

  const options = useMemo(() => {
    const columnEnumValues = Object.values(
      GraphQLTypes.PersistedQuerySortColumn,
    );
    return (
      columnEnumValues
        // Performing object.values on an enum returns (string | T)[] so we need
        // to filter out the unexpected strings to get the correct type
        .filter((column): column is GraphQLTypes.PersistedQuerySortColumn =>
          columnEnumValues.includes(column),
        )
        .map((column) => ({
          value: column,
          label: getSortColumnTitle(column),
        }))
    );
  }, []);

  return (
    <Popover isLazy>
      <PopoverTrigger>
        <Button
          variant={variant}
          className={className}
          size={size}
          leftIcon={
            sortDirection === GraphQLTypes.Ordering.ASCENDING ? (
              <IconSortAscending />
            ) : (
              <IconSortDescending />
            )
          }
          rightIcon={<IconChevronDown />}
        >
          {getSortColumnTitle(sortColumn)}
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="space-y-4 p-4">
          <div className="flex items-center justify-between">
            <Heading variant="title">Sort by</Heading>
            <Button
              variant="secondary"
              size="xs"
              leftIcon={
                sortDirection === GraphQLTypes.Ordering.ASCENDING ? (
                  <IconSortAscending />
                ) : (
                  <IconSortDescending />
                )
              }
              onClick={() =>
                setUrlSortParams({
                  sortDirection:
                    sortDirection === GraphQLTypes.Ordering.ASCENDING
                      ? GraphQLTypes.Ordering.DESCENDING
                      : GraphQLTypes.Ordering.ASCENDING,
                })
              }
            >
              {sortDirection === GraphQLTypes.Ordering.ASCENDING
                ? 'Ascending'
                : 'Descending'}
            </Button>
          </div>
          <div className="flex flex-col space-y-2">
            {options.map((option) => (
              <div
                key={option.value}
                className="-mx-2 flex cursor-pointer items-center rounded px-2 py-1 hover:bg-secondary"
                onClick={() =>
                  setUrlSortParams({
                    sortColumn: option.value,
                  })
                }
              >
                <Radio isChecked={sortColumn === option.value} />
                <span className="ml-2 flex-1">{option.label}</span>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
